import React, { useState, useEffect } from "react";
import { Button, Card, Tag, Divider } from "antd";
import {
  LeftOutlined,
  CheckOutlined,
  ShopOutlined,
  ToolOutlined,
  ShoppingCartOutlined,
  CoffeeOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import moment from "moment";
import GenerateTurnstileToken from "../../components/turnstile";

const customStyles = `
  /* Responsive styles for mobile devices */
  @media (max-width: 768px) {
    .summary-container {
      padding: 20px 16px !important;
    }

    .summary-card {
      padding: 24px 16px !important;
      margin: 0 !important;
    }

    .summary-header {
      padding-right: 0 !important;
      margin-bottom: 24px !important;
    }

    .summary-title {
      font-size: 20px !important;
    }

    .summary-section {
      margin-bottom: 20px !important;
    }

    .summary-buttons {
      flex-direction: column !important;
      gap: 12px !important;
    }
  }

  /* Desktop styles */
  @media (min-width: 769px) {
    .summary-container {
      padding: 96px 192px !important;
    }

    .summary-card {
      padding: 40px 32px !important;
    }

    .summary-title {
      font-size: 24px !important;
    }

    .summary-section {
      margin-bottom: 32px !important;
    }

    .summary-buttons {
      flex-direction: row !important;
      gap: 16px !important;
    }
  }
`;

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    window.addEventListener("resize", listener);
    return () => window.removeEventListener("resize", listener);
  }, [matches, query]);

  return matches;
};

const featureIcons = {
  track_inventory: <ShopOutlined />,
  manufacturing_enabled: <ToolOutlined />,
  pos_retail_enabled: <ShoppingCartOutlined />,
  pos_restaurant_enabled: <CoffeeOutlined />,
  location_enabled: <EnvironmentOutlined />,
  warehouse_enabled: <HomeOutlined />,
  multi_currency_enabled: <DollarOutlined />,
};

const featureNames = {
  track_inventory: "Track Inventory",
  manufacturing_enabled: "Manufacturing",
  pos_retail_enabled: "POS Retail",
  pos_restaurant_enabled: "POS Restaurant",
  location_enabled: "Multiple Locations",
  warehouse_enabled: "Multiple Warehouses",
  multi_currency_enabled: "Multi-Currency",
};

const Third = ({ formData, extra, setExtra, navigation }) => {
  const { previous, next } = navigation;
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [cloudflare_token, setCloudflareToken] = useState(null);
  const [turnstileError, setTurnstileError] = useState(false);

  const selectedFeatures = formData.features || [];
  const hasFeatures = selectedFeatures.length > 0;

  const handleNext = () => {
    if (!cloudflare_token) {
      console.error("No Cloudflare token available");
      setTurnstileError(true);
      return;
    }
    setExtra((prev) => ({
      ...prev,
      cloudflare_token: cloudflare_token,
    }));
    next();
  };

  const handleTurnstileError = () => {
    console.log("Turnstile verification failed");
    setCloudflareToken(null);
    setTurnstileError(true);
  };

  return (
    <div
      className="summary-container"
      style={{
        backgroundColor: "#EEF2F7",
        minHeight: "100vh",
        padding: isMobile ? "20px 16px" : "96px 192px",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
      }}
    >
      <style>{customStyles}</style>

      <Card
        className="summary-card"
        style={{
          backgroundColor: "#FAFAFA",
          borderRadius: "16px",
          boxShadow: "0px 28px 62px rgba(37, 78, 207, 0.03)",
          padding: isMobile ? "24px 16px" : "40px 32px",
          width: "100%",
          maxWidth: "800px",
          margin: "0 auto",
          border: "none",
        }}
      >
        <div
          className="summary-header"
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            marginBottom: "32px",
          }}
        >
          <div
            style={{
              flex: "1 1 0",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-start",
              gap: 2,
              display: "inline-flex",
            }}
          >
            <div
              className="summary-title"
              style={{
                alignSelf: "stretch",
                color: "#343C46",
                fontSize: isMobile ? 20 : 24,
                fontFamily: "Inter",
                fontWeight: "700",
                wordWrap: "break-word",
              }}
            >
              Review Your Organization
            </div>
            <div
              style={{
                color: "#636972",
                fontSize: 12,
                fontFamily: "Inter",
                fontWeight: "400",
                wordWrap: "break-word",
              }}
            >
              Please review the information below before submitting your
              registration
            </div>
          </div>
          <div style={{ width: 70, height: 40, position: "relative" }}>
            <img
              src="images/logo.svg"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
              alt="Tigg Logo"
            />
          </div>
        </div>

        <div className="summary-section">
          <h3
            style={{
              color: "#343C46",
              fontSize: 16,
              fontFamily: "Inter",
              fontWeight: "600",
              marginBottom: 16,
            }}
          >
            Organization Details
          </h3>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{ color: "#636972", fontSize: 14, fontFamily: "Inter" }}
              >
                Name:
              </span>
              <span
                style={{
                  color: "#343C46",
                  fontSize: 14,
                  fontFamily: "Inter",
                  fontWeight: "500",
                }}
              >
                {formData.name}
              </span>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{ color: "#636972", fontSize: 14, fontFamily: "Inter" }}
              >
                Workspace URL:
              </span>
              <span
                style={{
                  color: "#143E9F",
                  fontSize: 14,
                  fontFamily: "Inter",
                  fontWeight: "500",
                }}
              >
                {formData.slug}.tigg.app
              </span>
            </div>

            {formData.industry && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    color: "#636972",
                    fontSize: 14,
                    fontFamily: "Inter",
                  }}
                >
                  Industry:
                </span>
                <span
                  style={{
                    color: "#343C46",
                    fontSize: 14,
                    fontFamily: "Inter",
                    fontWeight: "500",
                  }}
                >
                  {formData.industry}
                </span>
              </div>
            )}

            {formData.email && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    color: "#636972",
                    fontSize: 14,
                    fontFamily: "Inter",
                  }}
                >
                  Email:
                </span>
                <span
                  style={{
                    color: "#343C46",
                    fontSize: 14,
                    fontFamily: "Inter",
                    fontWeight: "500",
                  }}
                >
                  {formData.email}
                </span>
              </div>
            )}

            {formData.phone && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    color: "#636972",
                    fontSize: 14,
                    fontFamily: "Inter",
                  }}
                >
                  Phone:
                </span>
                <span
                  style={{
                    color: "#343C46",
                    fontSize: 14,
                    fontFamily: "Inter",
                    fontWeight: "500",
                  }}
                >
                  {formData.phone}
                </span>
              </div>
            )}

            {formData.address && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <span
                  style={{
                    color: "#636972",
                    fontSize: 14,
                    fontFamily: "Inter",
                  }}
                >
                  Address:
                </span>
                <span
                  style={{
                    color: "#343C46",
                    fontSize: 14,
                    fontFamily: "Inter",
                    fontWeight: "500",
                    textAlign: "right",
                    maxWidth: "60%",
                  }}
                >
                  {formData.address}
                </span>
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{ color: "#636972", fontSize: 14, fontFamily: "Inter" }}
              >
                Accounting Start Date:
              </span>
              <span
                style={{
                  color: "#343C46",
                  fontSize: 14,
                  fontFamily: "Inter",
                  fontWeight: "500",
                }}
              >
                {extra.accounting_start_date
                  ? moment(extra.accounting_start_date).format("DD-MM-YYYY")
                  : "16-07-2025"}
              </span>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{ color: "#636972", fontSize: 14, fontFamily: "Inter" }}
              >
                VAT Registered:
              </span>
              <span
                style={{
                  color: "#343C46",
                  fontSize: 14,
                  fontFamily: "Inter",
                  fontWeight: "500",
                }}
              >
                {extra.vat_registered ? "Yes" : "No"}
              </span>
            </div>
          </div>
        </div>

        <Divider />

        <div className="summary-section">
          <h3
            style={{
              color: "#343C46",
              fontSize: 16,
              fontFamily: "Inter",
              fontWeight: "600",
              marginBottom: 16,
            }}
          >
            Selected Features
          </h3>

          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 16,
              width: "100%",
              flexWrap: isMobile ? "wrap" : "nowrap",
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: isMobile ? "100%" : "0",
              }}
            >
              {hasFeatures ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {selectedFeatures.map((feature) => (
                    <Tag
                      key={feature}
                      icon={featureIcons[feature]}
                      color="blue"
                      style={{
                        padding: "4px 12px",
                        borderRadius: 6,
                        fontSize: 12,
                        fontFamily: "Inter",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      {featureNames[feature] || feature}
                    </Tag>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    padding: "20px",
                    textAlign: "center",
                    backgroundColor: "#F6F8FA",
                    borderRadius: 8,
                    border: "1px dashed #E0E5EB",
                  }}
                >
                  <span
                    style={{
                      color: "#636972",
                      fontSize: 14,
                      fontFamily: "Inter",
                      fontStyle: "italic",
                    }}
                  >
                    No additional features selected - you can always enable them
                    later
                  </span>
                </div>
              )}
            </div>

            <div
              style={{
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8,
                minWidth: isMobile ? "100%" : "auto",
                marginTop: isMobile ? 16 : 0,
              }}
            >
              <GenerateTurnstileToken
                setToken={(token) => {
                  console.log(
                    "Turnstile token received:",
                    token ? "Valid" : "Invalid",
                  );
                  setCloudflareToken(token);
                  setTurnstileError(false);
                }}
                onError={handleTurnstileError}
              />

              {turnstileError && (
                <div
                  style={{
                    color: "#E82E49",
                    fontSize: "12px",
                    fontFamily: "Inter",
                    textAlign: "center",
                    maxWidth: 200,
                  }}
                >
                  Verification failed. Please try again by refreshing the page.
                </div>
              )}
            </div>
          </div>
        </div>

        <Divider />

        <div
          className="summary-buttons"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: isMobile ? "12px" : "16px",
            flexDirection: isMobile ? "column" : "row",
            width: "100%",
          }}
        >
          <Button
            icon={<LeftOutlined />}
            size="large"
            onClick={previous}
            style={{
              borderRadius: 6,
              minWidth: isMobile ? "100%" : 120,
              fontWeight: 600,
              fontFamily: "Inter",
              height: 48,
              fontSize: "12px",
            }}
          >
            Back
          </Button>

          <Button
            type="primary"
            size="large"
            icon={<CheckOutlined />}
            onClick={handleNext}
            disabled={!cloudflare_token}
            style={{
              height: 48,
              minWidth: isMobile ? "100%" : 200,
              background: cloudflare_token
                ? "var(--Main-blue, #143E9F)"
                : "#CBD0D6",
              boxShadow: cloudflare_token
                ? "0px 8px 24px -8px rgba(20, 62, 159, 0.16)"
                : "none",
              borderRadius: 6,
              fontWeight: 600,
              fontFamily: "Inter",
              fontSize: 14,
              lineHeight: "21px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Confirm & Submit
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default Third;
